"use strict";
/**
 * The subset of Telegram's HTML parse mode a staff member may paste into a broadcast (spec
 * 2026-10-28 broadcasts §4.1). One tokenizer serves the server-side gate and the panel preview,
 * so what the preview shows is what Telegram accepts. Nothing is interpolated into the text, so a bare `<` or `&` is an error rather than something
 * to escape. `<pre><code class="language-x">` is deliberately outside the subset.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.BROADCAST_TITLE_MAX_LENGTH = exports.BROADCAST_TEXT_MAX_LENGTH = void 0;
exports.parseTelegramHtml = parseTelegramHtml;
exports.validateTelegramHtml = validateTelegramHtml;
/** Telegram's limit on the rendered text; the raw string is never shorter, so capping it is safe. */
exports.BROADCAST_TEXT_MAX_LENGTH = 4096;
exports.BROADCAST_TITLE_MAX_LENGTH = 120;
const ALIASES = {
    b: 'b',
    strong: 'b',
    i: 'i',
    em: 'i',
    u: 'u',
    ins: 'u',
    s: 's',
    strike: 's',
    del: 's',
    code: 'code',
    pre: 'pre',
    a: 'a',
};
const TAG = /^<(\/?)([a-zA-Z]+)((?:\s+[a-zA-Z-]+="[^"]*")*)\s*>/;
const HREF = /^\s+href="([^"]*)"$/;
const SAFE_HREF = /^(https?:\/\/|tg:\/\/)/i;
const ENTITY = /^&(amp|lt|gt|quot|#39|#(\d{1,7})|#x([0-9a-fA-F]{1,6}));/;
const NAMED = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'" };
/** Decodes the entity at the start of `rest`, or null when it is not one Telegram accepts. */
function decodeEntity(rest) {
    const match = ENTITY.exec(rest);
    if (!match)
        return null;
    const [whole, name, decimal, hex] = match;
    if (name in NAMED)
        return { text: NAMED[name], length: whole.length };
    const code = decimal !== undefined ? Number(decimal) : Number.parseInt(hex, 16);
    if (!Number.isFinite(code) || code > 0x10ffff)
        return null;
    if (code >= 0xd800 && code <= 0xdfff)
        return null;
    if (code < 0x20 && code !== 9 && code !== 10)
        return null;
    return { text: String.fromCodePoint(code), length: whole.length };
}
/** Decodes every entity in an attribute value; a bare `&` is reported at `offset`. */
function decodeAttribute(value, offset, issues) {
    let out = '';
    for (let k = 0; k < value.length; k += 1) {
        if (value[k] !== '&') {
            out += value[k];
            continue;
        }
        const decoded = decodeEntity(value.slice(k));
        if (!decoded) {
            issues.push({ offset, message: '"&" must be written as &amp;' });
            out += '&';
            continue;
        }
        out += decoded.text;
        k += decoded.length - 1;
    }
    return out;
}
function pushText(children, text) {
    if (text === '')
        return;
    const last = children[children.length - 1];
    if (last && last.type === 'text')
        last.text += text;
    else
        children.push({ type: 'text', text });
}
function parseTelegramHtml(raw) {
    const root = [];
    const issues = [];
    const stack = [];
    const current = () => stack.length ? stack[stack.length - 1].node.children : root;
    const insideVerbatim = () => {
        const top = stack[stack.length - 1];
        return top && top.node.type === 'tag' && (top.node.tag === 'code' || top.node.tag === 'pre')
            ? top.node.tag
            : null;
    };
    let i = 0;
    while (i < raw.length) {
        const ch = raw[i];
        if (ch === '<') {
            const match = TAG.exec(raw.slice(i));
            if (!match) {
                issues.push({ offset: i, message: '"<" must be written as &lt;' });
                pushText(current(), '<');
                i += 1;
                continue;
            }
            const [whole, closing, rawName, attrs] = match;
            const name = rawName.toLowerCase();
            const canonical = ALIASES[name];
            const verbatim = insideVerbatim();
            if (verbatim && !(closing && stack[stack.length - 1].name === name)) {
                issues.push({ offset: i, message: `no tags inside <${verbatim}>` });
            }
            else if (!canonical) {
                issues.push({ offset: i, message: `unknown tag <${closing}${rawName}>` });
            }
            else if (closing) {
                const top = stack[stack.length - 1];
                if (!top)
                    issues.push({ offset: i, message: `unexpected </${rawName}>` });
                else if (top.name !== name)
                    issues.push({ offset: i, message: `expected </${top.name}> before </${rawName}>` });
                else
                    stack.pop();
            }
            else if (canonical === 'a' && stack.some((f) => f.node.type === 'link')) {
                issues.push({ offset: i, message: 'no <a> inside <a>' });
            }
            else if (canonical === 'a') {
                // The frame is pushed even when the href is bad, so the matching `</a>` closes it instead
                // of producing a second, misleading "unexpected </a>" issue. The node never carries an
                // href that failed validation.
                const href = HREF.exec(attrs);
                let value = '';
                if (attrs === '')
                    issues.push({ offset: i, message: '<a> needs an href' });
                else if (!href)
                    issues.push({ offset: i, message: '<a> takes only a double-quoted href' });
                else {
                    const decoded = decodeAttribute(href[1], i, issues);
                    if (SAFE_HREF.test(decoded))
                        value = decoded;
                    else
                        issues.push({ offset: i, message: 'link must start with http://, https:// or tg://' });
                }
                const node = { type: 'link', href: value, children: [] };
                current().push(node);
                stack.push({ name, offset: i, node });
            }
            else {
                if (attrs !== '')
                    issues.push({ offset: i, message: `<${rawName}> takes no attributes` });
                const node = { type: 'tag', tag: canonical, children: [] };
                current().push(node);
                stack.push({ name, offset: i, node });
            }
            i += whole.length;
            continue;
        }
        if (ch === '&') {
            const decoded = decodeEntity(raw.slice(i));
            if (!decoded) {
                issues.push({ offset: i, message: '"&" must be written as &amp;' });
                pushText(current(), '&');
                i += 1;
                continue;
            }
            pushText(current(), decoded.text);
            i += decoded.length;
            continue;
        }
        pushText(current(), ch);
        i += 1;
    }
    for (const frame of stack)
        issues.push({ offset: frame.offset, message: `unclosed <${frame.name}>` });
    return { nodes: root, issues };
}
/** The server-side gate: an empty list means Telegram will accept the markup. */
function validateTelegramHtml(raw) {
    return parseTelegramHtml(raw).issues;
}
//# sourceMappingURL=telegram-html.js.map